import type { WariantBadge } from "../components/ui/Badge.js";
import { ApiError, type SessionListEntry } from "./api.js";
import { czekaNaCzlowieka, czyOdpytywac, IN_PROGRESS, postepPipeline, rodzajToru } from "./sessionStatus.js";

/**
 * Wiersz listy analiz bez Reacta i bez DOM: to, co wspolne dla listy administratora (pages/SessionsPage.tsx) i listy
 * "Moje rozmowy" konta `user` (pages/uzytkownik/StartPage.tsx). Wyciagniete z SessionsPage zamiast kopiowane, zeby
 * wariant plakietki statusu, dlugosc nagrania i regula "kiedy pokazac pasek postepu" miala jedno zrodlo prawdy.
 *
 * Roznice miedzy widokami (etykiety statusu, ukladanie opisu, pokazywanie bledow z analizy) zostaja w widokach.
 */

/** ST-3: kolor nie niesie znaczenia sam - obok wariantu zawsze idzie etykieta statusu. */
export function wariantStatusu(status: string): WariantBadge {
  if (status === "DONE") return "success";
  if (status === "FAILED") return "danger";
  // Czeka na decyzje czlowieka: to nie praca w toku, tylko sygnal "wymaga Twojej akcji".
  if (status === "NO_SPEECH" || czekaNaCzlowieka(status)) return "warning";
  return "info";
}

export function formatujDlugosc(sec: number | null): string | null {
  // Brak dlugosci to brak danych, a nie zero - "?" w wierszu wygladalby jak usterka.
  if (sec == null) return null;
  // Zaokraglamy CALOSC, a nie osobno minuty i sekundy. durationSec jest ulamkowy
  // (Float z ffprobe), wiec 119.7 przy liczeniu osobno dawalo m=1 i Math.round(59.7)=60,
  // czyli "1:60" - znacznik czasu, ktory nie istnieje.
  const razem = Math.round(sec);
  const m = Math.floor(razem / 60);
  const s = razem % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Kod bledu do pokazania obok komunikatu (widok administratora): status i kod albo brak polaczenia. */
export function kodBleduListy(err: unknown): string {
  return err instanceof ApiError ? `${err.status} ${err.code}` : "brak połączenia z serwerem";
}

/** Czy lista ma odpytywac serwer: tak, dopoki cokolwiek sie liczy (czekanie na zatwierdzenie podzialu nie liczy sie). */
export function czyListaWymagaOdpytywania(sesje: readonly Pick<SessionListEntry, "status">[] | null): boolean {
  return sesje?.some((s) => czyOdpytywac(s.status)) ?? false;
}

/**
 * Postep wiersza listy. Czekanie na zatwierdzenie podzialu nie ma paska: nic sie nie liczy, a pasek sugerowalby prace,
 * ktorej nie ma. Status mowi, ze to ruch uzytkownika.
 */
export function postepWiersza(s: Pick<SessionListEntry, "inputKind" | "status" | "utteranceCount">): {
  wToku: boolean;
  zrobione: number;
  wszystkie: number;
  procent: number;
  tor: ReturnType<typeof rodzajToru>;
} {
  const tor = rodzajToru({ inputKind: s.inputKind, status: s.status, maWypowiedzi: s.utteranceCount > 0 });
  const { zrobione, wszystkie } = postepPipeline(s.status, tor);
  return {
    wToku: IN_PROGRESS.has(s.status) && !czekaNaCzlowieka(s.status),
    zrobione,
    wszystkie,
    procent: (zrobione / wszystkie) * 100,
    tor,
  };
}

// ---------------------------------------------------------------------------
// Konto `user`: etykiety bez nazw etapow technicznych
// ---------------------------------------------------------------------------

/**
 * Etykieta statusu dla zwyklego uzytkownika. Administrator widzi etapy pipeline'u ("pomiar mowy", "ocena brzmienia"),
 * uzytkownik tylko to, co go dotyczy: czeka, liczy sie, gotowe, wymaga jego zatwierdzenia albo sie nie udalo.
 */
export function etykietaStatusuUzytkownika(status: string): string {
  switch (status) {
    case "DONE":
      return "Gotowe";
    case "FAILED":
      return "Nie udało się";
    case "NO_SPEECH":
      return "Brak mowy";
    case "SPLIT_REVIEW":
      return "Do zatwierdzenia";
    case "QUEUED":
      return "W kolejce";
    default:
      // Kazdy inny status to praca w toku. Nieznany tez: lepsze "Oceniamy" niz surowy kod z bazy.
      return "Oceniamy";
  }
}

/** Dzien miesiaca w kalendarzu lokalnym: dwie daty z tego samego dnia daja ten sam klucz. */
function kluczDnia(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Data wiersza listy: "dziś, 11:40" dla dzisiejszej rozmowy, a dla starszych sama data "12.10.2026". Godzina starszej
 * rozmowy nic nie mowi, a "dziś" odrozni swieza rozmowe od tej o tej samej nazwie. Zla data to brak (null), nie "Invalid Date".
 */
export function dataWiersza(iso: string, teraz: Date = new Date()): string | null {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return null;
  if (kluczDnia(data) === kluczDnia(teraz)) {
    return `dziś, ${data.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })}`;
  }
  return data.toLocaleDateString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric" });
}
